import * as React from 'react';
import Grid from "@mui/material/Grid";
import ServiceLocalization from './ServiceLocalization';
import Vendor from 'data/Vendor';
import { Main } from 'styles/themeColors';
import Divider from 'components/UI/Divider';
import ServiceListingFilter, { ServiceListFilter } from './ServiceListFilter';
import ServiceListTable from './ServiceListTable';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import Modal from 'components/UI/Modal';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { ADDON_STATUS_CODE, SERVICE_CONSTANTS } from 'constants/service';
import ServiceListHeader from './ServiceListHeader';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { navigate } from 'hooks/useHookRouterCompat';
import { deleteServices } from 'requests/services';
import { getServiceTableColumns } from './ServiceTableColumns';
import { getItems } from 'lib/SessionStorage';
import { GENERAL_CONSTANTS, STORAGE_KEYS } from 'constants/general';
import { BusinessUnit } from 'data/BusinessUnit';
import FeatureFlag from 'data/FeatureFlag';

const EmptyMessage = styled.span`
  margin: 0px 20px;
  padding: 0px;
  font-size: 24px
`;

const ContainerGrid = muiStyled(Grid)({
    marginTop: '20px',
    width: '100%',
});

const GridMargin = muiStyled(Grid)({
    margin: '0 20px',
});

interface Props {
    selectedVendor: Vendor;
    selectedBusinessUnit?: BusinessUnit;
    featureFlags?: FeatureFlag;
}

const ServiceList = ({ selectedVendor, selectedBusinessUnit, featureFlags }: Props) => {

    const [loading, setLoading] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [filters, setFilters] = React.useState<ServiceListFilter>({
        publishStatus: { value: ServiceLocalization.listing.filter.all },
        country: [],
    });
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [reload, setReload] = React.useState(false);

    const countries = getItems(STORAGE_KEYS.COUNTRIES);
    const columns = getServiceTableColumns(countries);

    const handleFilterUpdate = (filters: ServiceListFilter) => {
        setFilters(filters);
    };

    const handleEdit = (id: number) => {
        navigate(`/addons/addon-details/${id}`);
    };

    const handleDelete = () => {
        if (selectedRows.length > 0) {
            if (selectedRows?.every((row) => row?.serviceStatus?.code === ADDON_STATUS_CODE.DRAFT)) setShowDeleteModal(true);
            else renderToast(toast.TYPE.ERROR, SERVICE_CONSTANTS.CANNOT_DELETE_PUBLISHED_SERVICE);
        }
    };

    const deleteReq = useAPI({
        deferFn: deleteServices,
        onResolve: () => {
            renderToast(toast.TYPE.SUCCESS, `${selectedRows.length === 1 ? SERVICE_CONSTANTS.SERVICE_DELETED : SERVICE_CONSTANTS.SERVICES_DELETED}`);
            setReload(true);
            setShowDeleteModal(false);
            setSelectedRows([]);
        },
        onReject: (err: any) => {
            renderToast(toast.TYPE.ERROR, err.message);
            setShowDeleteModal(false);
            setSelectedRows([]);
        }
    });

    const onDelete = () => {
        if (selectedRows.length > 0) {
            const ids = selectedRows.map((row) => row.id);
            deleteReq.run(ids);
        }
    }

    const confirmDeleteModal = (
        <Modal
            showModal={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onClick={() => onDelete()}
            title={`${GENERAL_CONSTANTS.REMOVE} ${selectedRows?.length === 1 ? SERVICE_CONSTANTS.SERVICE : SERVICE_CONSTANTS.SERVICES}`}
            primaryButtonText={GENERAL_CONSTANTS.CONFIRM}
            ghostButtonText={GENERAL_CONSTANTS.BACK}
            primaryButtonColor={Main.brandDark}
            color={Main.black}
            borderColor={Main.disabled}
        >
            <p>{`${SERVICE_CONSTANTS.ARE_YOU_SURE} ${selectedRows?.length === 1 ? GENERAL_CONSTANTS.THIS : GENERAL_CONSTANTS.THESE}  ${selectedRows?.length === 1 ? SERVICE_CONSTANTS.SERVICE : SERVICE_CONSTANTS.SERVICES}?`}</p>
        </Modal>
    )

    if (deleteReq.isLoading) {
        return <LoadingIndicator />;
    }

    const handleAddNewService = () => {
        navigate('/addons/new-addon');
    }

    return (
        <ContainerGrid container>
            {
                !selectedVendor ?
                    <EmptyMessage>{ServiceLocalization.emptyMessage}</EmptyMessage> :
                    !selectedBusinessUnit && featureFlags?.businessUnit?.enabled ?
                        <EmptyMessage>{SERVICE_CONSTANTS.SELECT_BUSINESS_UNIT_MESSAGE}</EmptyMessage> :
                        <>
                            <ServiceListHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClickNewService={handleAddNewService} selectedVendor={selectedVendor} />
                            <Divider height="1px" maxWidth='100%' marginY='8px' />
                            <ServiceListingFilter
                                selectedVendorId={selectedVendor ? selectedVendor.id : undefined}
                                filterUpdate={handleFilterUpdate}
                                loading={loading}
                            />

                            <GridMargin size={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <ServiceListTable
                                    serviceColumns={columns}
                                    filters={filters}
                                    searchTerm={searchTerm}
                                    selectedVendorId={selectedVendor ? selectedVendor.id : undefined}
                                    selectedBusinessUnitId={selectedBusinessUnit ? selectedBusinessUnit.id : undefined}
                                    setShowDeleteModal={setShowDeleteModal}
                                    setSelectedRows={setSelectedRows}
                                    selectedRows={selectedRows}
                                    reload={reload}
                                    setReload={setReload}
                                    setLoading={setLoading}
                                    handleEdit={handleEdit}
                                    handleDelete={handleDelete}
                                    loading={loading}
                                />
                                {confirmDeleteModal}
                            </GridMargin>
                        </>
            }
        </ContainerGrid>

    );
}

export default React.memo(ServiceList);